import {
  limitToLast,
  onDisconnect,
  onValue,
  push,
  query,
  ref,
  remove,
  serverTimestamp,
  set,
  update,
} from "firebase/database";
import { getFirebaseUser } from "./firebaseClient";

export async function createMovieChatSession({
  movieId,
  displayName,
  clientId,
  onPeople,
  onConnectionChange,
}) {
  const { database, user } = await getFirebaseUser();
  const presenceRef = ref(database, `movieChatPresence/${movieId}/${user.uid}`);
  const connectedRef = ref(database, ".info/connected");
  const roomRef = ref(database, `movieChatPresence/${movieId}`);

  const stopConnection = onValue(connectedRef, async (snapshot) => {
    const connected = snapshot.val() === true;
    onConnectionChange(connected);
    if (!connected) return;

    await onDisconnect(presenceRef).remove();
    await set(presenceRef, {
      uid: user.uid,
      name: displayName,
      clientId,
      joinedAt: serverTimestamp(),
    });
  });

  const stopPeople = onValue(roomRef, (snapshot) => {
    const normalizedName = displayName.trim().toLocaleLowerCase();
    const people = Object.values(snapshot.val() || {})
      .filter((person) => person.uid !== user.uid)
      .filter((person) => !clientId || person.clientId !== clientId)
      // Older presence entries predate clientId and can survive an anonymous-to-Google auth change.
      .filter((person) => person.clientId || person.name?.trim().toLocaleLowerCase() !== normalizedName)
      .sort((a, b) => a.name.localeCompare(b.name));
    onPeople(people);
  });
  const activeTypingRefs = new Map();

  return {
    uid: user.uid,
    subscribeToMessages(otherUid, callback) {
      const [participantA, participantB] = [user.uid, otherUid].sort();
      const messagesRef = query(
        ref(
          database,
          `movieChatMessages/${movieId}/${participantA}/${participantB}`
        ),
        limitToLast(100)
      );

      return onValue(messagesRef, (snapshot) => {
        const messages = [];
        snapshot.forEach((child) => {
          messages.push({ id: child.key, ...child.val() });
        });
        callback(messages);
      });
    },
    async sendMessage(otherUid, text) {
      const [participantA, participantB] = [user.uid, otherUid].sort();
      const messageRef = push(
        ref(
          database,
          `movieChatMessages/${movieId}/${participantA}/${participantB}`
        )
      );
      const message = {
        senderId: user.uid,
        recipientId: otherUid,
        senderName: displayName,
        text,
        createdAt: serverTimestamp(),
      };
      await update(ref(database), {
        [`movieChatMessages/${movieId}/${participantA}/${participantB}/${messageRef.key}`]:
          message,
        [`movieChatInbox/${movieId}/${otherUid}/${messageRef.key}`]: message,
      });
    },
    subscribeToInbox(callback) {
      const inboxRef = query(
        ref(database, `movieChatInbox/${movieId}/${user.uid}`),
        limitToLast(30)
      );
      return onValue(inboxRef, (snapshot) => {
        const notifications = [];
        snapshot.forEach((child) => {
          notifications.push({ id: child.key, ...child.val() });
        });
        callback(notifications);
      });
    },
    acknowledgeNotification(notificationId) {
      return remove(
        ref(
          database,
          `movieChatInbox/${movieId}/${user.uid}/${notificationId}`
        )
      );
    },
    subscribeToTyping(otherUid, callback) {
      const [participantA, participantB] = [user.uid, otherUid].sort();
      const typingRef = ref(
        database,
        `movieChatTyping/${movieId}/${participantA}/${participantB}/${otherUid}`
      );
      return onValue(typingRef, (snapshot) => callback(snapshot.exists()));
    },
    async setTyping(otherUid, isTyping) {
      const [participantA, participantB] = [user.uid, otherUid].sort();
      const typingRef = ref(
        database,
        `movieChatTyping/${movieId}/${participantA}/${participantB}/${user.uid}`
      );
      if (!isTyping) {
        activeTypingRefs.delete(otherUid);
        await remove(typingRef);
        return;
      }
      activeTypingRefs.set(otherUid, typingRef);
      await onDisconnect(typingRef).remove();
      await set(typingRef, {
        name: displayName,
        updatedAt: serverTimestamp(),
      });
    },
    async close() {
      stopConnection();
      stopPeople();
      await Promise.all(
        [...activeTypingRefs.values()].map((typingRef) =>
          remove(typingRef).catch(() => {})
        )
      );
      await remove(presenceRef);
    },
  };
}
